import React, { useState, useRef, useEffect } from 'react';
import { formatearMoneda } from '../utils/moneda';
import { PrinterIcon, XCircleIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { obtenerPrintConfig, guardarPrintConfig } from '../api/backend_v2';

const STORAGE_KEY = 'optica_print_config';

const defaultConfig = {
  nombre: 'OPTICA',
  direccion: '',
  telefono1: '',
  telefono2: '',
  email: '',
  logo: '',   // base64 data URL
};

// Helper: formats DD/MM/YYYY from ISO date string
const fmtFecha = (f) => {
  if (!f) return '';
  return String(f).slice(0, 10).split('-').reverse().join('/');
};

// Renders an underlined value or blank line
const Campo = ({ val, width = '28mm', label = '' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-end', marginRight: '2mm' }}>
    {label && <span style={{ marginRight: '0.5mm' }}>{label}</span>}
    <span
      style={{
        display: 'inline-block',
        minWidth: width,
        borderBottom: '0.5pt solid #333',
        paddingLeft: '1mm',
        paddingRight: '1mm',
        lineHeight: '1.4',
        verticalAlign: 'bottom',
      }}
    >
      {val || '\u00A0'}
    </span>
  </span>
);

// Dotted row separator line
const DottedLine = () => (
  <div style={{ borderTop: '1.5pt dashed #888', margin: '3mm 0' }} />
);

// ─── Main component ───────────────────────────────────────────────────────────

const PrintSobre = ({ anteojo, cliente, onClose }) => {
  const [prometidoPara, setPrometidoPara] = useState('');
  const [editandoConfig, setEditandoConfig] = useState(false);
  const [opticaConfig, setOpticaConfig] = useState(() => {
    try {
      return { ...defaultConfig, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
    } catch {
      return { ...defaultConfig };
    }
  });
  const [configTemp, setConfigTemp] = useState({ ...opticaConfig });
  const logoInputRef = useRef(null);

  // Cargar config desde la API al montar (sobreescribe localStorage si hay datos en BD)
  useEffect(() => {
    obtenerPrintConfig()
      .then(data => {
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          const merged = { ...defaultConfig, ...data };
          setOpticaConfig(merged);
          setConfigTemp(merged);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        }
      })
      .catch(() => { /* sin red: usa localStorage */ });
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setConfigTemp(p => ({ ...p, logo: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const guardarConfig = async () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configTemp));
    setOpticaConfig({ ...configTemp });
    setEditandoConfig(false);
    try {
      await guardarPrintConfig(configTemp);
    } catch {
      // Guardado local OK; el servidor no estaba disponible
    }
  };

  const handlePrint = () => window.print();

  const a = anteojo || {};
  const c = cliente || {};
  const op = opticaConfig;
  const nombreCliente = [c.nombre, c.apellido].filter(Boolean).join(' ');

  // ── WhatsApp icon (inline SVG for print compatibility) ─────────────────────
  const WaIcon = ({ size = '9pt' }) => (
    <svg
      viewBox="0 0 24 24"
      fill="#25D366"
      style={{ width: size, height: size, verticalAlign: 'middle', marginRight: '1mm', flexShrink: 0 }}
      aria-label="WhatsApp"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );

  // ── Subcomponent: Optica header (talón + stub) ──────────────────────────────
  const OpticaHeader = ({ compact = false }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: compact ? '0.1mm' : '0.1mm',
      }}
    >
      {/* Left: logo + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1mm', maxWidth: '65%' }}>
        {op.logo ? (
          <img
            src={op.logo}
            alt="Logo"
            style={{
              width: compact ? '25mm' : '18mm',
              height: compact ? '25mm' : '18mm',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        ) : null}
        <div
          style={{
            fontWeight: 'bold',
            fontSize: compact ? '12pt' : '16pt',
            letterSpacing: '0.5pt',
            lineHeight: 1.2,
          }}
        >
          {op.nombre || 'Optica'}
        </div>
      </div>
      {/* Center: contact info */}
      <div style={{ textAlign: 'center', fontSize: compact ? '8.5pt' : '9.5pt', lineHeight: 1.3 }}>
        {op.direccion && <div>{op.direccion}</div>}
        {op.telefono1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5mm' }}>
            <WaIcon size={compact ? '9pt' : '10pt'} />
            {op.telefono1}
          </div>
        )}
        {op.telefono2 && <div>☎ {op.telefono2}</div>}
        {op.email && <div>{op.email}</div>}
      </div>
      {/* Right: form number */}
      <div style={{ fontWeight: 'bold', fontSize: compact ? '11pt' : '13pt', textAlign: 'right' }}>
        {a.nro_sobre ? (
          <>
            <span style={{ fontSize: '9pt', fontWeight: 'normal' }}>Nº</span>{' '}
            <span>{a.nro_sobre}</span>
          </>
        ) : null}
      </div>
    </div>
  );

  // ── Subcomponent: LEJOS/CERCA row pair ──────────────────────────────────────
  const GraduacionSection = ({ titulo, esf_od, cil_od, eje_od, cristal, esf_oi, cil_oi, eje_oi, armazon }) => (
    <div style={{ marginBottom: '5mm' }}>
      {/* Title on its own line */}
      <div style={{ fontWeight: 'bold', fontSize: '10pt', marginBottom: '2mm' }}>{titulo}</div>
      {/* OD/OI measurements (left) + Lente/Armazón (right) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4mm', paddingLeft: '4mm' }}>
        {/* Left: basic measurements */}
        <div>
          {/* OD row */}
          <div style={{ marginBottom: '2mm', fontSize: '10pt', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 'bold', marginRight: '1mm' }}>O.D.</span>
            <Campo label="Esf" val={esf_od} width="14mm" />
            <Campo label="Cil" val={cil_od} width="14mm" />
            <Campo label="en" val={eje_od} width="14mm" />
          </div>
          {/* OI row */}
          <div style={{ fontSize: '10pt', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 'bold', marginRight: '1mm' }}>O.I.</span>
            <Campo label="Esf" val={esf_oi} width="14mm" />
            <Campo label="Cil" val={cil_oi} width="14mm" />
            <Campo label="en" val={eje_oi} width="14mm" />
          </div>
        </div>
        {/* Right: Lente and Armazón */}
        <div>
          <div style={{ marginBottom: '2mm', fontSize: '10pt' }}>
            <Campo label="Lente" val={cristal} width="28mm" />
          </div>
          <div style={{ fontSize: '10pt' }}>
            <Campo label="Armazón Art" val={armazon} width="38mm" />
          </div>
        </div>
      </div>
    </div>
  );

  // ── Subcomponent: Totales box ────────────────────────────────────────────────
  const TotalesBox = ({ style = {} }) => (
    <div style={{ textAlign: 'right', ...style }}>
      {[
        { label: 'TOTAL', val: a.precio_total },
        { label: 'SEÑA', val: a.sena },
        { label: 'SALDO', val: a.saldo },
      ].map(({ label, val }) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', marginBottom: '0.2mm' }}>
          <span style={{ fontWeight: 'bold', fontSize: '10pt', marginRight: '2mm', minWidth: '12mm' }}>{label}</span>
          <span
            style={{
              display: 'inline-block',
              width: '28mm',
              borderBottom: '0.5pt solid #333',
              paddingLeft: '1mm',
              textAlign: 'right',
              fontSize: '10.5pt',
            }}
          >
            {val != null && val !== '' ? formatearMoneda(val) : '\u00A0'}
          </span>
        </div>
      ))}
    </div>
  );

  // ── Print form content (shared between preview & print area) ─────────────────
  const FormContent = () => (
    <div
      style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        color: '#000',
        backgroundColor: '#fff',
        padding: '1mm 6mm',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      {/* ═══════ STUB (talón superior) ═══════════════════════════════════════ */}
      <div style={{ paddingBottom: '3mm' }}>
        <OpticaHeader compact />

        <div style={{ borderTop: '0.5pt solid #ccc', margin: '2mm 0' }} />

        {/* Nombre y Apellido */}
        <div style={{ marginBottom: '1.5mm', fontSize: '10pt' }}>
          <span style={{ fontWeight: 'bold' }}>Nombre y Apellido:</span>{' '}
          <Campo val={nombreCliente} width="120mm" />
        </div>

        {/* Domicilio / Tel */}
        <div style={{ marginBottom: '2mm', fontSize: '10pt' }}>
          <span style={{ fontWeight: 'bold' }}>Domicilio:</span>{' '}
          <Campo val={c.domicilio} width="90mm" />
          <span style={{ fontWeight: 'bold', marginLeft: '2mm' }}>Tel.:</span>{' '}
          <Campo val={c.telefono} width="28mm" />
        </div>

        {/* Fecha + Prometido + Totales */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          {/* Fecha box */}
          <div style={{ display: 'flex', gap: '3mm' }}>
            <div
              style={{
                border: '0.5pt solid #666',
                padding: '2mm 4mm',
                minWidth: '28mm',
                textAlign: 'center',
                fontSize: '9.5pt',
              }}
            >
              <div style={{ fontWeight: 'bold', borderBottom: '0.5pt solid #666', paddingBottom: '1mm', marginBottom: '1mm' }}>
                FECHA
              </div>
              <div style={{ minHeight: '8mm' }}>{fmtFecha(a.fecha_pedido)}</div>
            </div>
            <div
              style={{
                border: '0.5pt solid #666',
                padding: '2mm 4mm',
                minWidth: '40mm',
                textAlign: 'center',
                fontSize: '9.5pt',
              }}
            >
              <div style={{ fontWeight: 'bold', borderBottom: '0.5pt solid #666', paddingBottom: '1mm', marginBottom: '1mm' }}>
                PROMETIDO PARA EL DIA
              </div>
              <div style={{ minHeight: '8mm' }}>{prometidoPara ? fmtFecha(prometidoPara) : '\u00A0'}</div>
            </div>
          </div>

          {/* Totales */}
          <TotalesBox />
        </div>
      </div>

      {/* ═══════ DIVISOR PERFORADO ══════════════════════════════════════════ */}
      <DottedLine />

      {/* ═══════ FORMULARIO PRINCIPAL ═══════════════════════════════════════ */}
      <div>
        {/* Header principal */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2mm' }}>
          {/* Left: logo + name, en formulario principal */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '3mm', maxWidth: '55%' }}>
            {op.logo ? (
              <img
                src={op.logo}
                alt="Logo"
                style={{ width: '18mm', height: '18mm', objectFit: 'contain', flexShrink: 0 }}
              />
            ) : null}
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '15pt' }}>{op.nombre || 'OPTICA'}</div>
              <div style={{ fontSize: '8.5pt', lineHeight: 1.5 }}>
                {op.direccion && <div>{op.direccion}</div>}
                {op.telefono1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5mm' }}>
                    <WaIcon size="10pt" />
                    {op.telefono1}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fecha y Prometido: top right */}
          <div style={{ textAlign: 'right', fontSize: '9.5pt' }}>
            <div style={{ fontWeight: 'bold', fontSize: '13pt', marginTop: '1mm' }}>
              {a.nro_sobre ? <>Nº&nbsp;<span>{a.nro_sobre}</span></> : null}
            </div>
            <div style={{ marginBottom: '1mm' }}>
              <span style={{ fontWeight: 'bold' }}>FECHA</span>{' '}
              <Campo val={fmtFecha(a.fecha_pedido)} width="20mm" />
              {'  '}
              <span style={{ fontWeight: 'bold' }}>PROMETIDO PARA EL DIA</span>{' '}
              <Campo val={prometidoPara ? fmtFecha(prometidoPara) : ''} width="20mm" />
            </div>
          </div>
        </div>

        <div style={{ borderTop: '0.5pt solid #ccc', margin: '2mm 0' }} />

        {/* Paciente */}
        <div style={{ marginBottom: '1.5mm', fontSize: '10pt' }}>
          <span style={{ fontWeight: 'bold' }}>Paciente:</span>{' '}
          <Campo val={nombreCliente} width="100mm" />
        </div>

        {/* Domicilio / Tel */}
        <div style={{ marginBottom: '1.5mm', fontSize: '10pt' }}>
          <span style={{ fontWeight: 'bold' }}>Domicilio:</span>{' '}
          <Campo val={c.domicilio} width="80mm" />
          {'  '}
          <span style={{ fontWeight: 'bold' }}>Tel.:</span>{' '}
          <Campo val={c.telefono} width="28mm" />
        </div>

        {/* Localidad / e-mail */}
        <div style={{ marginBottom: '3mm', fontSize: '10pt' }}>
          <span style={{ fontWeight: 'bold' }}>Localidad:</span>{' '}
          <Campo val="" width="60mm" />
          {'  '}
          <span style={{ fontWeight: 'bold' }}>e-mail:</span>{' '}
          <Campo val={c.email} width="50mm" />
        </div>

        {/* ── GRADUACIONES + TOTALES en dos columnas ── */}
        <div style={{ display: 'flex', gap: '4mm', alignItems: 'flex-start' }}>
          {/* Left column: graduations */}
          <div style={{ flex: 1 }}>
            <GraduacionSection
              titulo="LEJOS"
              esf_od={a.lejos_esf_od}
              cil_od={a.lejos_cil_od}
              eje_od={a.lejos_eje_od}
              cristal={a.lejos_cristal}
              esf_oi={a.lejos_esf_oi}
              cil_oi={a.lejos_cil_oi}
              eje_oi={a.lejos_eje_oi}
              armazon={a.lejos_armazon}
            />

            <GraduacionSection
              titulo="CERCA"
              esf_od={a.cerca_esf_od}
              cil_od={a.cerca_cil_od}
              eje_od={a.cerca_eje_od}
              cristal={a.cerca_cristal}
              esf_oi={a.cerca_esf_oi}
              cil_oi={a.cerca_cil_oi}
              eje_oi={a.cerca_eje_oi}
              armazon={a.cerca_armazon}
            />

            {/* Bifocal/Adición row */}
            {['Multifocal', 'Bifocal', 'Ocupacional'].includes(a.tipo_anteojo) && (
              <div style={{ fontSize: '10pt', marginTop: '1mm', marginBottom: '2mm' }}>
                <Campo label="Bifocal" val={a.tipo_anteojo} width="24mm" />
                <Campo label="Di Lejos" val={a.od_adicion} width="14mm" />
                <Campo label="Di Cerca" val={a.oi_adicion} width="14mm" />
                <Campo label="Altura OD" val={a.altura_od} width="12mm" />
                <Campo label="OI" val={a.altura_oi} width="12mm" />
              </div>
            )}

            {/* Prismas row */}
            {(a.grados_prisma_od || a.grados_prisma_oi || a.base_od || a.base_oi) && (
              <div style={{ fontSize: '10pt', marginTop: '1mm', marginBottom: '2mm', whiteSpace: 'nowrap' }}>
                <Campo label="Prisma OD" val={a.grados_prisma_od} width="14mm" />
                <Campo label="Base" val={a.base_od} width="14mm" />
                <Campo label="Prisma OI" val={a.grados_prisma_oi} width="14mm" />
                <Campo label="Base" val={a.base_oi} width="14mm" />
              </div>
            )}


            {/* Observaciones */}
            <div style={{ fontSize: '10pt', marginBottom: '1mm' }}>
              <span style={{ fontWeight: 'bold' }}>Observaciones</span>
            </div>
            <div
              style={{
                border: '0.5pt solid #666',
                minHeight: '24mm',
                padding: '1.5mm',
                fontSize: '9.5pt',
                marginBottom: '2mm',
                whiteSpace: 'pre-wrap',
              }}
            >
              {a.observaciones || '\u00A0'}
            </div>
          </div>

          {/* Right column: totals */}
          <div style={{ minWidth: '42mm', paddingTop: '16mm' }}>
            <TotalesBox />

            {/* Extra info */}
            {(a.usa_seguro || a.usa_promocion || a.tipo_pago) && (
              <div style={{ fontSize: '9pt', marginTop: '3mm', lineHeight: 1.6 }}>
                {a.usa_seguro && <div>✓ Seguro</div>}
                {a.usa_promocion && <div>✓ Promoción</div>}
                {a.tipo_pago && <div>✓ {{ efectivo: 'Efectivo', tarjeta_debito: 'Tarjeta Débito', tarjeta_credito: 'Tarjeta Crédito', transferencia: 'Transferencia' }[a.tipo_pago] || a.tipo_pago}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Global print styles ── */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-sobre-area, .print-sobre-area * { visibility: visible !important; }
          .print-sobre-area {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            z-index: 99999 !important;
          }
          .no-print { display: none !important; }
          @page { margin: 8mm; size: A4 portrait; }
        }
      `}</style>

      {/* ── Screen overlay (hidden during print) ── */}
      <div className="no-print fixed inset-0 bg-gray-700 bg-opacity-75 z-[1000] flex flex-col items-center overflow-y-auto pb-8 pt-16">
        {/* Controls bar */}
        <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg px-5 py-4 sticky top-4 z-10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-base font-semibold text-gray-900">
              Imprimir Sobre{a.nro_sobre ? ` Nº ${a.nro_sobre}` : ''}
              {nombreCliente ? ` — ${nombreCliente}` : ''}
            </h3>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => { setConfigTemp({ ...opticaConfig }); setEditandoConfig(v => !v); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded font-medium"
              >
                <Cog6ToothIcon className="h-4 w-4" /> Datos Óptica
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium shadow"
              >
                <PrinterIcon className="h-4 w-4" /> Imprimir
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded font-medium"
                >
                  <XCircleIcon className="h-4 w-4" /> Cerrar
                </button>
              )}
            </div>
          </div>

          {/* Prometido para el día */}
          <div className="mt-3 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Prometido para el día:
            </label>
            <input
              type="date"
              value={prometidoPara}
              onChange={e => setPrometidoPara(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {prometidoPara && (
              <button
                onClick={() => setPrometidoPara('')}
                className="text-xs text-gray-500 hover:text-red-600"
              >
                ✕ borrar
              </button>
            )}
          </div>
        </div>

        {/* Optica config panel */}
        {editandoConfig && (
          <div className="bg-amber-50 border border-amber-200 w-full max-w-3xl p-4 shadow">
            <h4 className="font-medium text-amber-800 mb-3 text-sm">
              Datos de encabezado del formulario (se guarda en este navegador)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'nombre', label: 'Nombre de la óptica' },
                { key: 'direccion', label: 'Dirección' },
                { key: 'telefono1', label: 'Teléfono 1' },
                { key: 'telefono2', label: 'Teléfono 2' },
                { key: 'email', label: 'E-mail' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="text"
                    value={configTemp[key]}
                    onChange={e => setConfigTemp(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              ))}
            </div>

            {/* Logo upload */}
            <div className="mt-4 border-t border-amber-200 pt-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Logo de la óptica</label>
              <div className="flex items-center gap-4">
                {configTemp.logo ? (
                  <div className="relative">
                    <img
                      src={configTemp.logo}
                      alt="Logo preview"
                      className="h-16 w-16 object-contain border border-gray-200 rounded bg-white p-1"
                    />
                    <button
                      onClick={() => { setConfigTemp(p => ({ ...p, logo: '' })); if (logoInputRef.current) logoInputRef.current.value = ''; }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center leading-none"
                      title="Eliminar logo"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs text-center">
                    Sin logo
                  </div>
                )}
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload-input"
                  />
                  <label
                    htmlFor="logo-upload-input"
                    className="cursor-pointer inline-flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 font-medium"
                  >
                    📂 Seleccionar imagen
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG — se guarda en el navegador</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={guardarConfig}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Guardar
              </button>
              <button
                onClick={() => setEditandoConfig(false)}
                className="px-3 py-1.5 text-sm bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Form preview */}
        <div
          className="mt-4 shadow-xl bg-white border border-gray-200"
          style={{ width: '210mm', minHeight: '297mm' }}
        >
          <FormContent />
        </div>
      </div>

      {/* ── Print-only area (always in DOM, only visible when printing) ── */}
      <div className="print-sobre-area">
        <FormContent />
      </div>
    </>
  );
};

export default PrintSobre;
